<template>
  <v-card>
    <v-card-title class="primary">
      <span class="headline white--text">
        {{ $t('extendTicketBook') }}
      </span>
      <v-spacer/>
      <Icon
        iconColor="white"
        icon="mdi-close"
        :small="false"
        large
        :tooltipText="$t('close')"
        @icon-clicked="$emit('close')"/>
    </v-card-title>
    <v-card-text class="mt-6">
      <FormWrapper
      :buttonText="$t('extend')"
      :disabled="!canExtend"
      @submit="confirm">
        <p class="subtitle-1 black--text font-weight-medium mb-0">
          {{ $t('extendTicketBookBody', { ticketBookName: ticketBook.name }) }}
        </p>
        <p class="subtitle-1 black--text">
          {{ $t('pages') }}: {{ currentRange }}
        </p>
        <v-text-field
          v-model="newLastPageString"
          :label="$t('newLastPage')"
          type="number"
          hide-spin-buttons
          outlined
          dense
          color="black"
          class="mb-2"
          :error-messages="errorMessages"
          :hint="createsPagesHint"
          persistent-hint
        />
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { TicketBookStatus } from '@/utils/Enumerations.js'
import { pageRangeString, numberWithCommas } from '@/utils/NumericMutations'
import { TicketBookConstants } from '@/utils/constants.js'

export default {
  name: 'TicketBookExtendDialog',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue')
  },

  props: {
    ticketBook: {
      type: Object,
      required: true
    },
    ticketBooks: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    newLastPageString: ''
  }),

  computed: {
    currentRange () {
      return pageRangeString(this.ticketBook.firstPage, this.ticketBook.lastPage)
    },

    newLastPage () {
      return parseInt(this.newLastPageString)
    },

    hasValidNewLastPage () {
      return !isNaN(this.newLastPage) && this.newLastPage > this.ticketBook.lastPage
    },

    exceedsExtensionLimit () {
      return this.hasValidNewLastPage && this.newLastPage - this.ticketBook.lastPage > TicketBookConstants.MaxNewPagesPerBook
    },

    overlapsNextBook () {
      return this.hasValidNewLastPage && this.nextBook !== undefined && this.newLastPage >= this.nextBook.firstPage
    },

    hasValidProposal () {
      return this.hasValidNewLastPage && !this.exceedsExtensionLimit
    },

    errorMessages () {
      if (this.newLastPageString !== '' && !this.hasValidNewLastPage) {
        return this.$t('mustBeGreaterThanNumber', { number: this.ticketBook.lastPage })
      }
      if (this.exceedsExtensionLimit) {
        return this.$t('extendTicketBookPageCountExceeded', { max: TicketBookConstants.MaxNewPagesPerBook })
      }
      if (this.overlapsNextBook) {
        return this.nextBookBlockedText
      }
      return undefined
    },

    booksAhead () {
      return this.ticketBooks
        .filter(tb =>
          tb.ticketBookId !== this.ticketBook.ticketBookId &&
          tb.status !== TicketBookStatus.Inactive.value &&
          tb.totalPageCount > 0 &&
          tb.firstPage > this.ticketBook.lastPage)
        .sort((a, b) => a.firstPage - b.firstPage)
    },

    nextBook () {
      return this.booksAhead[0]
    },

    nextBookBlockedText () {
      return this.$t('extendTicketBookNextBookBlocked', {
        pageNumber: numberWithCommas(this.nextBook.firstPage - 1)
      })
    },

    createsPagesHint () {
      if (!this.canExtend) return ''
      return this.$t('extendTicketBookCreatesPages', {
        pages: pageRangeString(this.ticketBook.lastPage + 1, this.newLastPage)
      })
    },

    canExtend () {
      return this.hasValidProposal && !this.overlapsNextBook
    }
  },

  methods: {
    confirm () {
      if (!this.canExtend) return
      const pages = []
      for (let pageNumber = this.ticketBook.lastPage + 1; pageNumber <= this.newLastPage; pageNumber++) {
        pages.push({ pageNumber })
      }
      this.$emit('confirm', {
        ticketBookId: this.ticketBook.ticketBookId,
        pages
      })
    }
  }
}
</script>
